<template>
  <div class="custome-photo-album">
    <div>
      <b>
        Please click 'SUBMIT PAGE' after customizing each page to save your
        customization.
      </b>
    </div>
    <sib-button
      @click="submitPhotoAlbum"
      class="mt-sm"
      type="button"
      role="primary"
      size="md"
      width="auto"
      :loading="uploadPhotoAlbumPageLoading"
    >
      SUBMIT PAGE
    </sib-button>

    <p
      class="result-message mb-0"
      :class="{
        'result-message--success': isSuccess,
        'result-message--error': !isSuccess,
      }"
      v-if="resultMessage"
    >
      {{ resultMessage }}
    </p>

    <input
      type="hidden"
      v-if="mediaCampaignStore.photoAlbum.length"
      :name="`properties[${PHOTO_ALBUM_ATTRIBUTE_KEY}]`"
      :value="mediaCampaignStore.photoAlbum.map((item) => item.image)"
    />
  </div>
</template>

<script lang="ts" setup>
import useProductStore from '@/store/product'
import { computed, onMounted, ref, watch } from 'vue'
import {
  CUSTOMILY_CANVAS_WRAPPER_CLASS,
  CUSTOMILY_PREVIEW_CANVAS_ID,
  CUSTOMIZE_EACH_PAGE_TITLE,
  EVENT_ERROR_FILL_OUT_ALL_PAGE_PHOTO_ALBUM,
  PHOTO_ALBUM_ATTRIBUTE_KEY,
  PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ID,
  PRODUCT_TYPE_DIGITAL_PHOTO_BOOK,
} from '@/utils'
import Logger from '@/services/log'

import useMediaCampaignStore from '@/store/media'
import Provider from '@/provider'
import EventBus from '@/services/eventbus'
import { isProductHasType } from '@/utils/product'

const mediaCampaignStore = useMediaCampaignStore()
const productStore = useProductStore()
const uploadPhotoAlbumPageLoading = ref(false)
const resultMessage = ref('')
const isSuccess = ref(false)
let timeOut: any = null

const isProductTypeDigitalPhotoBook = computed(() =>
  isProductHasType(productStore?.product, [PRODUCT_TYPE_DIGITAL_PHOTO_BOOK])
)

onMounted(() => {
  EventBus.on(EVENT_ERROR_FILL_OUT_ALL_PAGE_PHOTO_ALBUM, () => {
    resultMessage.value = `You've forgotten to enter information for some pages, please check again.`
    isSuccess.value = false
  })

  watch(
    () => resultMessage.value,
    () => {
      if (resultMessage.value) {
        if (timeOut) clearTimeout(timeOut)
        timeOut = setTimeout(() => {
          resultMessage.value = ''
        }, 5000)
      }
    }
  )
})

async function getCustomilyOptionWrapper() {
  const findParentElement = () => {
    const parentElements = document.querySelectorAll('.customily_option')
    for (let parent of parentElements) {
      let child = parent.querySelector('.option_name')
      if (
        child &&
        child.textContent &&
        child.textContent.includes(CUSTOMIZE_EACH_PAGE_TITLE)
      ) {
        return parent
      }
    }
    return null
  }

  const result = findParentElement()

  return result
}

async function getPagePhotoAlbumSelectedValue(wrapper: any) {
  const radios = wrapper.querySelectorAll('input[type="radio"]')
  // return element?.value || null

  let checkedIndex = -1

  radios.forEach((radio, index) => {
    if (radio.checked) {
      checkedIndex = index + 1
    }
  })

  return checkedIndex
}

async function uploadPhotoAlbum(
  base64: string,
  page: string | number
): Promise<void> {
  const provider = await Provider.getInstance()
  try {
    const link = await provider.uploadImageBase64(base64)
    if (typeof link == 'string') {
      resultMessage.value = 'Submit successfully.'
      isSuccess.value = true
    } else {
      resultMessage.value = 'Something went wrong. Please try again later.'
      isSuccess.value = false
    }
    updatePhotoAlbum(page, link)
  } catch (err) {
    Logger.log('Error when uploading file', err)
  }
}

function updatePhotoAlbum(page: string | number, link: string) {
  const index = mediaCampaignStore.photoAlbum.findIndex(
    (item) => item.page == page
  )
  const photo = {
    page,
    image: link,
  }
  if (index > -1) {
    mediaCampaignStore.photoAlbum.splice(index, 1, photo)
  } else {
    mediaCampaignStore.photoAlbum.push(photo)
  }
  mediaCampaignStore.photoAlbum.sort((a: any, b: any) => a.page - b.page)
}

async function getPreviewImageUrl() {
  const originalCanvas: HTMLCanvasElement | null = document.querySelector(
    `#${CUSTOMILY_PREVIEW_CANVAS_ID}`
  ) as HTMLCanvasElement

  if (originalCanvas && isProductTypeDigitalPhotoBook.value)
    return originalCanvas.toDataURL('image/png', 1.0)

  if (originalCanvas) {
    const originalContext = originalCanvas.getContext('2d')
    if (originalContext) {
      const originalImageData = originalContext.getImageData(
        0,
        0,
        originalCanvas.width,
        originalCanvas.height
      )
      // Calculate the new height
      const newWidth = 1000
      const newHeight =
        (originalCanvas.height / originalCanvas.width) * newWidth

      // Create new canvas
      const newCanvas = document.createElement('canvas')
      newCanvas.width = newWidth
      newCanvas.height = newHeight
      const newContext = newCanvas.getContext('2d')

      if (newContext) {
        // Enlarge the image
        newContext.imageSmoothingEnabled = true
        newContext.imageSmoothingQuality = 'high' // Use high quality when zooming
        newContext.drawImage(
          originalCanvas,
          0,
          0,
          originalCanvas.width,
          originalCanvas.height,
          0,
          0,
          newWidth,
          newHeight
        )
      }
      return newCanvas.toDataURL()
    }
  }

  // fallback if can not create new canvas
  return productStore.productPreviewImage
}

function checkCustomilyFileInput() {
  const removeFileButtons = document.querySelectorAll(
    '.customily-file-input .cl_remove-image'
  )
  const isHidden = (element: any) => {
    return element.style.display == 'none'
  }
  for (let button of removeFileButtons) {
    if (button && isHidden(button)) {
      resultMessage.value = 'Please fill out all required fields.'
      return false
    }
  }
  return true
}

async function handleUploadPhotoAlbum() {
  resultMessage.value = ''
  uploadPhotoAlbumPageLoading.value = true
  isSuccess.value = false

  const customilyOptionWrapper = await getCustomilyOptionWrapper()
  if (!customilyOptionWrapper) {
    uploadPhotoAlbumPageLoading.value = false
    return
  }
  const pagePhotoSelected = await getPagePhotoAlbumSelectedValue(
    customilyOptionWrapper
  )

  const customilyProductCanvasImageURL = await getPreviewImageUrl()
  if (!pagePhotoSelected || !customilyProductCanvasImageURL) {
    uploadPhotoAlbumPageLoading.value = false
    return
  }

  await uploadPhotoAlbum(customilyProductCanvasImageURL, pagePhotoSelected)
  uploadPhotoAlbumPageLoading.value = false
}

async function submitPhotoAlbum() {
  const result = checkCustomilyFileInput()
  if (!result) return

  if (isProductTypeDigitalPhotoBook.value) {
    uploadPhotoAlbumPageLoading.value = true

    // Enlarge the preview canvas wrapper so the uploaded image is not blurry
    const previewCanvasWrapperSizeEnlagre = 1000
    handleSizePreivewCanvasWrapper(previewCanvasWrapperSizeEnlagre)

    setTimeout(async () => {
      await handleUploadPhotoAlbum()

      // Set default size preview canvas wrapper
      handleSizePreivewCanvasWrapper()
    }, 1000)
  } else {
    await handleUploadPhotoAlbum()
  }
}

function handleSizePreivewCanvasWrapper(size?: number) {
  const canvasWrapper = document.querySelector(
    `#${PRODUCT_MAIN_SLIDER_PREVIEW_SLIDE_ID} .${CUSTOMILY_CANVAS_WRAPPER_CLASS}`
  )
  if (!canvasWrapper) return
  if (size) {
    // Enlarge the preview canvas so the uploaded image is not blurry
    canvasWrapper.style.width = `${size}px`
  } else {
    canvasWrapper.style.width = 'unset'
  }
}
</script>

<style lang="scss">
.custome-photo-album {
  padding: 0 1em 2em 1em;
  border-top: none;
  background-color: var(--background-color);

  .sib-button {
    width: 146px;
  }

  .result-message {
    font-size: 12px;
    font-weight: 500;
    &--success {
      color: var(--color-primary);
    }
    &--error {
      color: var(--color-secondary);
    }
  }
}
</style>
